import { useMutation, useQuery } from '@tanstack/react-query';
import { apiClient } from '../client';
import { useChatStore } from '../../store/chatStore';
import type { ChatHistoryResponse, ChatMessage, ChatSessionSummary } from '../../types/chat';

export function useNewChatSession() {
  const { clearMessages } = useChatStore();

  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<{ session_id: string }>('/chat/sessions/new');
      return data;
    },
    onSuccess: () => clearMessages(),
  });
}

export function useChatSessions(enabled = true) {
  return useQuery({
    queryKey: ['chat-sessions'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ sessions: ChatSessionSummary[] }>('/chat/sessions');
      return data.sessions;
    },
    enabled,
  });
}

export function useChatHistoryHydration(enabled: boolean, sessionId?: string | null) {
  return useQuery({
    queryKey: ['chat-history', sessionId ?? 'active'],
    queryFn: async () => {
      const { data } = await apiClient.get<ChatHistoryResponse>('/chat/history', {
        params: sessionId ? { session_id: sessionId } : undefined,
      });
      const messages: ChatMessage[] = data.messages.flatMap((message, index) => {
        const timestamp = new Date();
        const textMessage: ChatMessage = {
          id: `hist-${data.session_id}-${index}`,
          type: message.role,
          content: message.content,
          timestamp,
        };
        if (message.role !== 'assistant' || !message.platform_cards?.length) {
          return [textMessage];
        }
        return [
          textMessage,
          {
            id: `hist-${data.session_id}-${index}-cards`,
            type: 'action_result',
            content: '',
            timestamp,
            platformCards: message.platform_cards,
          },
        ];
      });
      if (sessionId) {
        useChatStore.getState().loadMessages(messages);
      } else {
        useChatStore.getState().hydrateMessages(messages);
      }
      return data;
    },
    enabled,
    // Selecting a session must always replace the visible transcript, even
    // when a previously viewed session is already in the query cache.
    staleTime: 0,
  });
}
